import React from 'react';
import styled, { css } from 'styled-components';
import { IconCircleInfoSolid } from '@/app/common/icons/libraryIcons';

import { useTranslation } from '@/app/components/translations/Translation';
import TransportJourneySummary, {
  FootprintBadgeContainer,
  FootprintContainer,
} from '@/app/components/hotel-detail/flight-train/TransportJourneySummary';
import { DictionaryProvider, FootprintBadge } from '@lmn/lmn-components';
import { Button, InfoContainer } from '@lmn/design-system';
import { URLParameters } from '@/app/common/urlParameters';
import { useSearchParams } from 'next/navigation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { TransportCardPriceContainer, TransportCardPriceContainerMobile } from '../changeTransport/ChangeTransportCard';
import { MoreTripThanHotelNightsMessage } from './MoreTripThanHotelNightsMessage';
import { useChangeTransportDetailsOpenModal } from '../changeTransport/ChangeTransportDetailsModalProvider';
import { useOffersContext } from '../OffersProvider';
import { useTransportNotAvailable } from './TransportNotAvailableProvider';
import { useIsLgOrGreater } from '../hooks/useMediaSSR';
import DebugBertolliFormula from '../DebugBertolliFormula';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';

const StyledButton = styled(Button)`
  width: 100%;
  min-height: 48px;
`;

const transportJourneySummaryMobileStyles = css`
  flex-grow: 1;
`;

// #endregion

export type TransportCardProps = {
  transportData: TransportEndpointResponse;
} & React.HTMLAttributes<HTMLElement>;

export const TransportCard: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<TransportCardProps>>
> = (props: TransportCardProps) => {
  const { transportData } = props;
  const selectedTransport: TransportsType = transportData.transports[0];
  const { t, tripCardDictionary } = useTranslation();
  const { offersData } = useOffersContext();
  const { openModal: openDetailsTransportModal } = useChangeTransportDetailsOpenModal();
  const { currentTransportNotAvailable } = useTransportNotAvailable();
  const isMobile = !useIsLgOrGreater();
  const continueIfAvailable = () => !currentTransportNotAvailable && openDetailsTransportModal(selectedTransport);
  const searchParams = useSearchParams();
  const debug = searchParams.get(URLParameters.DEBUG);
  const { isTrain } = useUrlSearchParams();
  const { localization } = usePageWrapperData();

  return isMobile ? (
    <div
      css={css`
        background-color: white;
        border-radius: 8px;
      `}
    >
      {debug && !isTrain && (
        <DebugBertolliFormula
          flightId={selectedTransport.id}
          cheapestPrice={offersData?.cheapestMealPlan?.finalPrice}
        />
      )}
      <TransportJourneySummary
        outbound={selectedTransport.outbound}
        inbound={selectedTransport.inbound}
        co2Footprint={selectedTransport.co2Footprint}
        transportId={selectedTransport.id}
        openDetailsClicked={continueIfAvailable}
        numberOfBaggagePiecesIncluded={selectedTransport.numberOfBaggagePiecesIncluded ?? undefined}
        css={transportJourneySummaryMobileStyles}
      />
      {currentTransportNotAvailable ? (
        <InfoContainer
          hasBorder={true}
          icon={IconCircleInfoSolid}
          type={'warning'}
          message={t('vacation.select.flight.message.flight_not_available')}
          css={css`
            margin: 0 16px 16px 16px;
          `}
        />
      ) : (
        <>
          <DictionaryProvider labels={tripCardDictionary}>
            <TransportCardPriceContainerMobile $hideBorder={currentTransportNotAvailable}>
              <StyledButton variant="primary" data-testid="select-flight-button" onClick={continueIfAvailable}>
                {t('vacation.select.details.book')}
              </StyledButton>
            </TransportCardPriceContainerMobile>
          </DictionaryProvider>
          <MoreTripThanHotelNightsMessage
            inbound={selectedTransport.inbound}
            outbound={selectedTransport.outbound}
            onMessageClicked={continueIfAvailable}
            highlighted={true}
          />
        </>
      )}
    </div>
  ) : (
    <div
      css={css`
        background-color: white;
        border-radius: 8px;
      `}
    >
      {debug && !isTrain && (
        <DebugBertolliFormula
          flightId={selectedTransport.id}
          cheapestPrice={offersData?.cheapestMealPlan?.finalPrice}
        />
      )}
      <div
        css={css`
          display: flex;
          flex-direction: row;
        `}
      >
        <TransportJourneySummary
          outbound={selectedTransport.outbound}
          inbound={selectedTransport.inbound}
          co2Footprint={selectedTransport.co2Footprint}
          transportId={selectedTransport.id}
          openDetailsClicked={continueIfAvailable}
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            flex-grow: 1;
          `}
          numberOfBaggagePiecesIncluded={selectedTransport.numberOfBaggagePiecesIncluded ?? undefined}
        />
        <DictionaryProvider labels={tripCardDictionary}>
          <TransportCardPriceContainer onClick={continueIfAvailable}>
            {selectedTransport.co2Footprint && selectedTransport.co2Footprint.co2kg > 0 ? (
              <FootprintContainer>
                <FootprintBadgeContainer $desktop={!isMobile}>
                  <FootprintBadge
                    footprint={selectedTransport.co2Footprint}
                    useShortVariant={true}
                    locale={localization.locale}
                  />
                </FootprintBadgeContainer>
              </FootprintContainer>
            ) : null}
            <div
              css={`
                display: flex;
                flex-direction: column;
                gap: 8px;
                width: 100%;
                margin-top: auto;
              `}
            >
              <StyledButton
                disabled={currentTransportNotAvailable ?? false}
                variant="primary"
                data-testid="select-flight-button"
              >
                {t('vacation.select.details.book')}
              </StyledButton>
            </div>
          </TransportCardPriceContainer>
        </DictionaryProvider>
      </div>
      {currentTransportNotAvailable ? (
        <InfoContainer
          hasBorder={true}
          icon={IconCircleInfoSolid}
          type={'warning'}
          message={t('vacation.select.flight.message.flight_not_available')}
          css={css`
            margin: 12px 16px;
          `}
        />
      ) : null}
      <MoreTripThanHotelNightsMessage
        inbound={selectedTransport.inbound}
        outbound={selectedTransport.outbound}
        onMessageClicked={continueIfAvailable}
        highlighted={true}
      />
    </div>
  );
};
